import type { ComponentProps } from 'react';
import React from 'react';

import { Dialog } from '@rtkwlf/fenrir-react';
import { useTranslation } from 'react-i18next';
import { useBlocker } from 'react-router-dom';

import { useBeforeUnload } from './use-before-unload/use-before-unload';

export interface PageBlockerProps {
  shouldBlock?: boolean;
  title?: string;
  description?: string;
  cancelText?: string;
  confirmText?: string;
  appearance?: ComponentProps<typeof Dialog.FooterAction>['appearance'];
}

export const PageBlocker = ({
  title,
  description,
  cancelText,
  confirmText,
  appearance,
  shouldBlock = false,
}: PageBlockerProps) => {
  const { t } = useTranslation();

  useBeforeUnload(shouldBlock);
  const blocker = useBlocker(shouldBlock);

  const messages = {
    cancelText: t('confirmation:cancel'),
    confirmText: t('confirmation:leavePage.leave'),
    description: t('confirmation:leavePage.unsavedChanges'),
    title: t('confirmation:leavePage.title'),
  };

  const handleConfirm = () => {
    blocker.proceed?.();
  };

  const handleCancel = () => {
    blocker.reset?.();
  };

  return (
    <Dialog.Root isOpen={blocker.state === 'blocked'}>
      <Dialog.Content>
        <Dialog.Header>
          <Dialog.HeaderTitle>{title ?? messages.title}</Dialog.HeaderTitle>
          <Dialog.HeaderDescription>
            {description ?? messages.description}
          </Dialog.HeaderDescription>
        </Dialog.Header>
        <Dialog.Footer>
          <Dialog.FooterCancel onClick={handleCancel}>
            {cancelText ?? messages.cancelText}
          </Dialog.FooterCancel>
          <Dialog.FooterAction appearance={appearance} onClick={handleConfirm}>
            {confirmText ?? messages.confirmText}
          </Dialog.FooterAction>
        </Dialog.Footer>
        <Dialog.Close onClick={handleCancel} />
      </Dialog.Content>
    </Dialog.Root>
  );
};
